/**
 * Cab Module – Booking confirmation & payment at /cabs/booking.
 * Uses location.state from CabSearchPage. Payment: wallet or card.
 * Wallet: uses existing wallet flow (check balance, deduct via API).
 */

import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";
import { FaCar } from "react-icons/fa";

const API_BASE = import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "";

export default function CabBookingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const payload = location.state || {};
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (!payload.serviceTypeId || !payload.pickupAddress || !payload.dropAddress || !payload.pickupTime || payload.totalAmount == null) {
      navigate("/cabs/search");
    }
  }, [user, payload, navigate]);

  const handleBook = async () => {
    if (!user) return;
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/cab/user/book`,
        {
          serviceTypeId: payload.serviceTypeId,
          cabTypeId: payload.cabTypeId || undefined,
          countryId: payload.countryId || undefined,
          cityId: payload.cityId || undefined,
          pickupAddress: payload.pickupAddress,
          dropAddress: payload.dropAddress,
          pickupTime: payload.pickupTime,
          baseFare: payload.baseFare,
          totalKm: payload.totalKm,
          perKmRate: payload.perKmRate,
          peakMultiplier: payload.peakMultiplier,
          nightMultiplier: payload.nightMultiplier,
          totalAmount: payload.totalAmount,
          customerName: user.username,
          customerEmail: user.email,
          customerPhone: user.phone || "",
          paymentMethod,
        },
        { withCredentials: true }
      );
      toast.success("Cab booked successfully!");
      navigate("/cabs/my-bookings", { state: { booking: res.data?.data } });
    } catch (e) {
      toast.error(e.response?.data?.error || "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user || !payload.serviceTypeId) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <FaCar className="text-blue-600" />
          Confirm Booking
        </h1>
        <div className="bg-white rounded-xl shadow p-6 space-y-4">
          <p><span className="text-gray-500">Pickup:</span> {payload.pickupAddress}</p>
          <p><span className="text-gray-500">Drop:</span> {payload.dropAddress}</p>
          <p><span className="text-gray-500">Time:</span> {payload.pickupTime ? new Date(payload.pickupTime).toLocaleString() : ""}</p>
          <p className="text-xl font-bold">₹ {payload.totalAmount}</p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Payment</label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="card">Card</option>
              <option value="wallet">Wallet</option>
            </select>
          </div>
          <button
            type="button"
            onClick={handleBook}
            disabled={submitting}
            className="w-full py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? "Booking…" : "Confirm Book"}
          </button>
        </div>
      </div>
    </div>
  );
}
